import { componentText, SchmancyElement } from '@schmancy/mixins/index'
import { css, html } from 'lit'
import { customElement, property, query } from 'lit/decorators.js'
import { fromEvent, merge, switchMap, takeUntil, tap, zip } from 'rxjs'
import '../button/button'

@customElement('schmancy-tree')
export class SchmancyTree extends SchmancyElement {
	static styles = [
		css`
			:host {
				display: block;
				position: relative;
				background-color: initial;
			}
			::slotted([slot='root']) {
				width: 100%;
				text-align: left;
			}
			::slotted([slot='root'] + *) {
				margin-top: 0.5rem;
			}
		`,
	]
	@property({ type: Boolean }) open = false

	/**
	 * Names this node, and through it the disclosure control — which otherwise
	 * reads the text the `root` slot renders. One of the two has to say which node
	 * this is: a tree of twenty nodes puts twenty disclosure controls on the page,
	 * and `Expand` names none of them. Set it when the root slot renders no text of
	 * its own (an icon-only row), or to name the node something other than what it
	 * shows.
	 */
	@property({ type: String }) label = ''

	@query('#toggler') toggler!: HTMLSlotElement
	@query('slot:not([name="root"])') defaultSlot!: HTMLSlotElement

	// #chevron is a <schmancy-button>; typed as HTMLElement rather than pulling in its class.
	@query('#chevron') chevron!: HTMLElement

	private readonly a11yId = `schmancy-tree-${Math.random().toString(36).slice(2, 10)}`
	private get contentId() {
		return `${this.a11yId}-content`
	}

	/** ElementInternals — broadcasts `:state(open)` for consumer CSS. */
	private readonly internals: ElementInternals | undefined = (() => {
		try {
			return this.attachInternals()
		} catch {
			return undefined
		}
	})()

	updated(changed: Map<string, unknown>) {
		super.updated?.(changed)
		if (changed.has('open')) {
			if (this.open) this.internals?.states.add('open')
			else this.internals?.states.delete('open')
		}
	}

	connectedCallback() {
		super.connectedCallback()
		// Re-wire on RECONNECT: firstUpdated ran once and the per-connection
		// `disconnecting` tore down the prior connection's toggle subscription on
		// the move-out; `hasUpdated` is false before the first render (firstUpdated
		// owns that), true on every reparent.
		if (this.hasUpdated) this.wireToggle()
	}

	firstUpdated() {
		if (!this.open) {
			this.defaultSlot.hidden = true
		}

		this.wireToggle()
	}

	/**
	 * Wires the toggler/chevron click pipeline. Called from firstUpdated
	 * (shadow DOM ready) and again from connectedCallback on reconnect.
	 */
	private wireToggle() {
		const toggleClick$ = fromEvent<MouseEvent>(this.toggler, 'click').pipe(
			takeUntil(this.disconnecting),
			tap(e => {
				e.preventDefault()
				e.stopPropagation()
				this.dispatchEvent(new CustomEvent('toggle', { bubbles: false, composed: true }))
			}),
		)

		const chevronClick$ = fromEvent<MouseEvent>(this.chevron, 'click')

		merge(toggleClick$, chevronClick$)
			.pipe(
				switchMap(() => {
					const fromDeg = this.open ? 180 : 0
					const toDeg = this.open ? 0 : 180
					const chevronAnimation = this.chevron.animate(
						[{ transform: `rotate(${fromDeg}deg)` }, { transform: `rotate(${toDeg}deg)` }],
						{
							duration: 150,
							easing: 'ease-in',
							fill: 'forwards',
						},
					)

					if (!this.open) {
						// Opacity can't animate on a hidden element — unhide before the animation runs.
						this.defaultSlot.hidden = false
					}

					const fromOpacity = this.open ? 1 : 0
					const toOpacity = this.open ? 0 : 1

					const slotAnimation = this.defaultSlot.animate([{ opacity: fromOpacity }, { opacity: toOpacity }], {
						duration: 150,
						easing: 'ease-out',
						fill: 'forwards',
					})

					// `this.open` still holds the pre-toggle value here — the flip happens
					// in the tap() below, after this animation completes.
					slotAnimation.onfinish = () => {
						if (this.open) {
							this.defaultSlot.hidden = true
						} else {
							this.defaultSlot.style.height = 'auto'
							this.defaultSlot.style.opacity = '1'
						}
					}

					return zip(fromEvent(chevronAnimation, 'finish'), fromEvent(slotAnimation, 'finish')).pipe(
						takeUntil(this.disconnecting),
					)
				}),
				tap(() => {
					this.open = !this.open
				}),
				takeUntil(this.disconnecting),
			)
			.subscribe()
	}

	render() {
		// Read from the light DOM rather than the slot's assignedNodes: this runs
		// on the first render too, before the slot element exists to be queried.
		const subject =
			this.label ||
			Array.from(this.children)
				.filter(child => child.getAttribute('slot') === 'root')
				.map(child => child.textContent ?? '')
				.join(' ')
				.replace(/\s+/g, ' ')
				.trim()

		return html`
			<div class="flex content-center items-center justify-between">
				<slot id="toggler" name="root" @slotchange=${() => this.requestUpdate()}></slot>

				<!-- Stop propagation so this click doesn't also bubble into the toggler above. -->
				<schmancy-button
					slot="trailing"
					id="chevron"
					aria-expanded=${this.open ? 'true' : 'false'}
					aria-controls=${this.contentId}
					aria-label=${subject
						? componentText(this.open ? 'tree.collapse' : 'tree.expand', { name: subject })
						: componentText(this.open ? 'tree.collapseUnnamed' : 'tree.expandUnnamed')}
					@click=${(e: Event) => e.stopPropagation()}
				>
					⌅
				</schmancy-button>
			</div>

			<slot id=${this.contentId}></slot>
		`
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'schmancy-tree': SchmancyTree
	}
}
